const GriddyDraw = require("../../../trainer/banvasView/util/GriddyDraw");
const GridGeometry = require("../../../trainer/util/GridGeometry");
const CanvasMath = require("../CanvasMath");

const colors = require("../../util/ColorUtil").getDiagramColors();

const DEFAULT_PATH_COLOR = colors.royalblue;
const DEFAULT_START_DOT_COLOR = colors.royalblue;
const DEFAULT_END_DOT_COLOR = colors.royalblue;
const DIAGONAL_DIRECTIONS = GriddyDraw.DIAGONAL_DIRECTIONS;

const START = "start";
const END = "end";
const BOTH = "both";

const CELL_LEN_SCALE = 0.8; // what the default cell length means relative to the font

const START_TEXT = "Start";
const END_TEXT = "Finish";

const DEFAULT_GRID_FONT_SIZE_FACTOR = 1;
const DEFAULT_PATH_FONT_SIZE_FACTOR = 0.8;

const DEFAULT_LABEL_PAD = 0.8; // padding between labels and grid as factor of font size

const LINE_WIDTH_FACTOR = 2;
const DOT_RADIUS_FACTOR = 2; // dot radius as factor of line width

/* packageItems()
 * Some args (ex. paths, dots) can either be a single item OR an array of items, where an item is a
 * array of properties that specify how to draw something, ex. the color and direction of a path.
 * This function takes these and packages single items into an array with the items for easier
 * processing.
 */
const packageItems = items => {
	if (!Array.isArray(items) || items.length === 0) {
		return [];
	} else if (!Array.isArray(items[0])) {
		return [items];
	} else return items;
};

/* getGridRowCol()
 * Returns the Grid row and column depending on the cartesian X and Y.
 */
const getRowColFromCartesian = (cartX, cartY, numCols, numRows) => {
	return [numRows - cartY, cartX];
};

/* drawPath()
 * Draws the specified path. Paths are lines with optional beginning and end dots (so, have nubbins).
 */
const drawPath = (
	context,
	geometry,
	pathItem,
	numCols,
	numRows,
	pathFontSizeOpt
) => {
	const [cartX, cartY, , colorOpt, , nubbinOpt = BOTH] = pathItem;

	const nubbinSpec = nubbinOpt.toLowerCase();

	// draw starting dot (aka nubbin) if specified
	if (nubbinSpec === START || nubbinSpec === BOTH) {
		drawDot(context, geometry, [cartX, cartY, colorOpt], numCols, numRows);
	}

	const rowCol = drawLine(
		context,
		geometry,
		pathItem,
		numCols,
		numRows,
		pathFontSizeOpt
	);

	// draw ending dot (aka nubbin) if specified
	if (nubbinSpec === END || nubbinSpec === BOTH) {
		drawDot(
			context,
			geometry,
			[null, null, colorOpt, rowCol],
			numCols,
			numRows
		);
	}
};

const drawPaths = (
	context,
	geometry,
	paths,
	numCols,
	numRows,
	pathFontSizeOpt
) => {
	if (!paths) return;
	for (const path of paths) {
		drawPath(context, geometry, path, numCols, numRows, pathFontSizeOpt);
	}
};

const drawDot = (context, geometry, dotItem, numCols, numRows) => {
	const [cartX, cartY, colorOpt, rowColOpt] = dotItem;

	const [row, col] =
		rowColOpt || getRowColFromCartesian(cartX, cartY, numCols, numRows);
	const color = colors[colorOpt] || DEFAULT_PATH_COLOR;

	GriddyDraw.drawVertexDot(context, geometry, colors, col, row, {
		colorOpt: color,
		radiusOpt: context.lineWidth * LINE_WIDTH_FACTOR * DOT_RADIUS_FACTOR,
	});
};

const drawDots = (context, geometry, dots, numCols, numRows) => {
	if (!dots) return;
	for (const dotItem of dots) {
		drawDot(context, geometry, dotItem, numCols, numRows);
	}
};

const drawLine = (
	context,
	geometry,
	lineItem,
	numCols,
	numRows,
	pathFontSizeOpt
) => {
	const [cartX, cartY, pathStr, colorOpt, numberOpt] = lineItem;

	const pathFontSize =
		pathFontSizeOpt || parseInt(context.font) * DEFAULT_PATH_FONT_SIZE_FACTOR;

	// parse optional fields
	const color = colors[colorOpt] || DEFAULT_PATH_COLOR;
	const drawNumbers = !!numberOpt;

	// build selected edges, diagonals, and path labels
	let [row, col] = getRowColFromCartesian(cartX, cartY, numCols, numRows);
	let pathLen = 0;
	const selectedEdges = {};
	const selectedDiagonals = {};
	const pathLabels = {};
	for (const move of pathStr.toUpperCase().split(" ")) {
		// parse edge (one character moves - L, R, U, and D)
		if (move.length === 1) {
			pathLen += 1;
			let edge;
			switch (move) {
				case "L":
					col--;
					pathLabels[`${col},${row},top`] = pathLen;
					edge = `${col},${row},true`;
					break;
				case "R":
					pathLabels[`${col},${row},top`] = pathLen;
					edge = `${col},${row},true`;
					col++;
					break;
				case "U":
					row--;
					pathLabels[`${col},${row},left`] = pathLen;
					edge = `${col},${row},false`;
					break;
				case "D":
					pathLabels[`${col},${row},left`] = pathLen;
					edge = `${col},${row},false`;
					row++;
					break;
				default:
					// do nothing
					break;
			}
			selectedEdges[edge] = color;
			// parse diagonal (two char moves - LU, LD, RU, and RD)
		} else if (move.length === 2) {
			pathLen += 1;
			let diag;
			switch (move) {
				case "LU":
				case "UL":
					diag = `${col},${row},${DIAGONAL_DIRECTIONS["LU"]}}`;
					row--;
					col--;
					break;
				case "LD":
				case "DL":
					diag = `${col},${row},${DIAGONAL_DIRECTIONS["LD"]}}`;
					row++;
					col--;
					break;
				case "RU":
				case "UR":
					diag = `${col},${row},${DIAGONAL_DIRECTIONS["RU"]}}`;
					row--;
					col++;
					break;
				case "RD":
				case "DR":
					diag = `${col},${row},${DIAGONAL_DIRECTIONS["RD"]}}`;
					row++;
					col++;
					break;
				default:
					// do nothing
					break;
			}
			selectedDiagonals[diag] = color;
		} // else move.length !== 1 and !== 2, do nothing
	}

	/* draw stuff */

	// draw rectangular edges
	GriddyDraw.drawSelectedEdges(
		context,
		geometry,
		colors,
		selectedEdges,
		null,
		null,
		{widthMultiplier: LINE_WIDTH_FACTOR}
	);
	// draw diagonals
	GriddyDraw.drawSelectedDiagonals(
		context,
		geometry,
		colors,
		selectedDiagonals,
		null,
		{widthMultiplier: LINE_WIDTH_FACTOR}
	);
	// draw path labels
	if (drawNumbers) {
		for (const label in pathLabels) {
			// parse
			const [numCol, numRow, type] = label
				.split(",")
				.map(s => s.trim())
				.map(s => (Number.isInteger(s) ? parseInt(s) : s));
			const num = pathLabels[label];

			GriddyDraw.drawNumberInCell(
				context,
				geometry,
				colors,
				num,
				numCol,
				numRow,
				null,
				false,
				pathFontSize,
				{
					[type]: true,
					drawOnEdge: true,
					backgroundCircle: true,
					circleColor: color,
				}
			);
		}
	}

	// return ending coordinates of line
	return [row, col];
};

/* drawCityWalks()
 * See BaTeX guide for most up-to-date documentation. Diagram function for CityWalks engine and BaTeX.
 *
 * numCols: number of columns of squares in grid
 * numRows: number of rows of squares in grid
 * paths: single path item OR array of path items
 *   - path item is [startX, startY, pathStr, [colorOptional], [numberOptional], [nubbinOptional]]
 *     - startX: the cartesian X to start the path. bottom edge of the grid is treated as the 0 x axis
 *     - startY: the cartesian Y to start the path. left edge of the grid is treated as the 0 y axis
 *     - pathStr: the path string, formated as space-separated L, R, U, D, or combinations thereof
 *       - L stands for left, R for right, U for up, and D for down, respectively
 *       - e.g. "L U R D" would draw a square
 *     - colorOptional: optional color for line
 *     - numberOptional: whether to number the segments of the line
 *     - nubbinOptional: whether to have nubbins at the start, end, or neither (none)
 *       - e.g. "start" would put a nubbin at the beginning of the line, "end" ant the end, "none" at neither
 * [options]: optional options object with the following field
 *   showLabels: whether to show labels. defaults to false
 *   hideDefaultDots: whether to hide the start and end dots. defaults to false
 *   dots: single dot item OR array of dot items
 *     - dot item is [x, y, [colorOptional]]
 *   x: distance from left border to draw CityWalks in canvas units
 *   y: distance from top border to draw CityWalks in canvas units
 *   w: width of CityWalks diagram in canvas units
 *   h: height of CityWalks diagram in canvas units
 *   cellLen: the length of a side of a cell in the grid as a function of cell size. defaults to the largest possible that can fit in a diagram
 *     - note that setting this can push text outside the bounds of the diagram specified by the ctx or x, y, w, and h
 *   tileWidth: width of each tile
 *      note: overrides font gridFontSizeFactor - labels will shrink if they don't fit
 *   hPad: extra horizontal padding
 *   vPad: extra vertical padding
 *   gridFontSizeFactor: the value to multiply font size for grid text
 *   pathFontSizeFactor: how much to scale the path by
 */
const drawCityWalks = function(context, numCols, numRows, paths, options = {}) {
	// setting defaults
	const diagramSize = context.getSize();
	const x = options.x || 0;
	const y = options.y || 0;
	const w = options.w || diagramSize.width;
	const h = options.h || diagramSize.height;

	// parsing
	const hideDefaultDots = Boolean(options.hideDefaultDots);
	const showLabels = Boolean(options.showLabels);
	paths = packageItems(paths);
	const dots = packageItems(options.dots);

	// calculating font size
	const fontSize = parseInt(context.font);
	const gridFontSizeFactor =
		options.gridFontSizeFactor || DEFAULT_GRID_FONT_SIZE_FACTOR;
	const gridFontSize = fontSize * gridFontSizeFactor;
	const pathFontSizeFactor =
		options.pathFontSizeFactor || DEFAULT_PATH_FONT_SIZE_FACTOR;
	const pathFontSize = fontSize * pathFontSizeFactor;

	// calculate padding
	const labelPad = DEFAULT_LABEL_PAD * gridFontSize;
	const startLabelWidth = CanvasMath.measureBatex(context, {
		batex: "Start",
		fontInfo: {size: gridFontSize},
	}).measure.width;
	const endLabelMeasure = CanvasMath.measureBatex(context, {
		batex: "Finish",
		fontInfo: {size: gridFontSize},
	}).measure;
	const endLabelWidth = endLabelMeasure.width;
	const labelHeight = endLabelMeasure.height;

	// grid size and rect calculations

	// default: fit 4 columns within 170-wide cell (no labels)
	// 170 was came up with via guess and check, and allows to fit 3 diagrams on the same line in a multiple choice problem
	const defaultCellLen =
		(170 - context.lineWidth * LINE_WIDTH_FACTOR * DOT_RADIUS_FACTOR * 2) / 4;
	const cellLen = options.cellLen
		? Number(options.cellLen) * fontSize * CELL_LEN_SCALE
		: defaultCellLen;
	// dimensions included below
	const gridW = numCols * cellLen;
	const gridH = numRows * cellLen;
	const gridX = x + (w - gridW) / 2;
	const gridY = y + (h - gridH) / 2;

	// build calculations
	const geometry = new GridGeometry(
		"square",
		numCols,
		numRows,
		options.geomProps || {}
	).withDimFromRect({x: gridX, y: gridY, w: gridW, h: gridH});

	/* draw stuff */

	if (options.removedEdges) {
		GriddyDraw.drawPartialGrid(context, geometry, colors, options.removedEdges);
	} else {
		GriddyDraw.drawGrid(context, geometry, colors);
	}

	// draw start and end dots
	if (!hideDefaultDots) {
		drawDot(
			context,
			geometry,
			[0, numRows, DEFAULT_START_DOT_COLOR],
			numCols,
			numRows
		);
		drawDot(
			context,
			geometry,
			[numCols, 0, DEFAULT_END_DOT_COLOR],
			numCols,
			numRows
		);
	}

	// draw start and end labels
	if (showLabels) {
		const startLabelRect = geometry.getEdgeLabelRect(
			0,
			0,
			startLabelWidth,
			labelHeight,
			labelPad
		);
		const endLabelRect = geometry.getEdgeLabelRect(
			geometry.cols,
			geometry.rows,
			endLabelWidth,
			labelHeight,
			labelPad
		);
		context.save();
		context.fillStyle = colors.defaultBlack;
		let drawOptions = {
			batex: START_TEXT,
			rect: startLabelRect,
		};
		CanvasMath.drawInRect(context, drawOptions);
		drawOptions = {
			batex: END_TEXT,
			rect: endLabelRect,
		};
		CanvasMath.drawInRect(context, drawOptions);
		context.restore();
	}

	// draw user-specified paths, lines, and dots. order matters - want to draw user-specified dots and lines on top
	drawPaths(context, geometry, paths, numCols, numRows, pathFontSize);
	drawDots(context, geometry, dots, numCols, numRows);

	return geometry;
};

module.exports = {
	drawCityWalks,
};
